import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { SplitButton, Dropdown, Spinner } from 'react-bootstrap';
import { FaRegThumbsUp } from 'react-icons/fa';
import { BiError } from 'react-icons/bi';
import { getErrorMessage } from 'Utils';
import AppContext from 'Utils/AppContext';
import { listSamplingRuleTemplates } from 'DataDiscovery/samplingRulesApi';
import { buildDiscoveryRequest, startDiscovery } from 'DataDiscovery/discoverApi';
import { ensureConnectionTested } from 'Utils/connectionGate';
import { useCanEdit, READ_ONLY_TITLE } from 'Auth/permissions';

/**
 * Pick the default sampling rule from the list returned by the API. Built-in
 * presets are seeded by the server and treated as the canonical default; if no
 * built-in is present (older servers / pruned templates) fall back to the first
 * row.
 */
function pickDefaultRule(rules) {
  if (!Array.isArray(rules) || rules.length === 0) return null;
  return rules.find((r) => r.builtIn === true) || rules[0];
}

/**
 * Inline Run Discovery split button for the data sources table and editor. The main
 * button starts a run with the currently selected sampling rule via the unified
 * orchestration-job protocol (POST /api/v1/discover/jobs); the dropdown lets the user
 * switch rules before clicking. Works for relational and every file-based datasourceType
 * (FILE_DELIMITED_FOLDER, FILE_PARQUET_FOLDER, FILE_GENERIC_FOLDER, …) — the platform
 * routes by datasourceType under the hood.
 *
 * onRunStarted is optional — callers can use it to refresh their local list after a
 * successful start.
 */
const RunDiscoveryButton = ({ dataSource, onRunStarted }) => {
  const { setNotification, addOptimisticRun } = useContext(AppContext);
  const history = useHistory();
  const [rules, setRules] = useState([]);
  const [rulesLoaded, setRulesLoaded] = useState(false);
  const [selectedRuleId, setSelectedRuleId] = useState(null);
  const [inFlight, setInFlight] = useState(false);
  const canEdit = useCanEdit();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listSamplingRuleTemplates();
        if (cancelled) return;
        setRules(list);
        const def = pickDefaultRule(list);
        if (def) setSelectedRuleId(def.id);
      } catch (e) {
        if (cancelled) return;
        console.log('Failed to load sampling rules', e);
      } finally {
        if (!cancelled) setRulesLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selectedRule = useMemo(
    () => rules.find((r) => r.id === selectedRuleId) || null,
    [rules, selectedRuleId]
  );

  const disabled = !rulesLoaded || rules.length === 0 || inFlight || !canEdit;

  const handleSuccess = (runId) => {
    setNotification({
      header: 'Discovery started',
      icon: <FaRegThumbsUp size='25px' color='#16A34A' />,
      body: (
        <span>
          Started discovery for <Link to='/discover'>{dataSource.name}</Link>.
        </span>
      ),
    });
    if (typeof onRunStarted === 'function') onRunStarted(runId);
    // Take the user to the Data Discovery page once the run is queued, so they
    // land where the in-progress run shows up rather than staying on the editor.
    history.push('/discover');
  };

  const handleError = (e, fallback) => {
    console.log('Run discovery failed', e);
    const msg = getErrorMessage(e);
    setNotification({
      header: 'Discovery failed',
      icon: <BiError size='25px' color='#5C6473' />,
      body: <span>{(typeof msg === 'string' && msg) || fallback}</span>,
    });
  };

  const handleRun = async () => {
    if (disabled) return;
    setInFlight(true);
    try {
      const gate = await ensureConnectionTested(dataSource, { history, setNotification });
      if (!gate.ok) return;
      const label = `${dataSource.name} – ${new Date().toISOString()}`;
      const request = buildDiscoveryRequest(dataSource, selectedRule, label);
      const result = await startDiscovery(request);
      const runId = result?.id ?? result?.run_id ?? null;
      if (typeof addOptimisticRun === 'function') {
        addOptimisticRun({
          searchName: label,
          connection: dataSource.name,
          type: dataSource.type || dataSource.engine,
          dataSourceId: dataSource.id,
          sampleRules: selectedRule?.name,
        });
      }
      handleSuccess(runId);
    } catch (e) {
      handleError(e, 'Failed to start discovery.');
    } finally {
      setInFlight(false);
    }
  };

  const buttonLabel = inFlight ? (
    <span><Spinner as='span' animation='border' size='sm' className='mr-2' />Starting…</span>
  ) : (
    'Run Discovery'
  );

  // SplitButton's title is the dropdown caret; the main click target shows
  // buttonLabel via children. The dropdown items are sampling rule rows.
  // A disabled button shows no tooltip of its own, so the wrapper carries the read-only reason.
  return (
    <span className='d-inline-block' title={!canEdit ? READ_ONLY_TITLE : undefined}>
      <SplitButton
        id={`run-discovery-${dataSource.id || 'unknown'}`}
        title={buttonLabel}
        variant='primary'
        size='sm'
        onClick={handleRun}
        disabled={disabled}
      >
        {rules.length === 0 && rulesLoaded && (
          <Dropdown.Item disabled>No sampling rules available</Dropdown.Item>
        )}
        {rules.length > 0 && (
          <Dropdown.Header style={{ fontFamily: 'Rubik' }}>Sampling rule</Dropdown.Header>
        )}
        {rules.map((r) => (
          <Dropdown.Item
            key={r.id}
            active={r.id === selectedRuleId}
            onClick={() => setSelectedRuleId(r.id)}
            style={{ fontFamily: 'Rubik' }}
          >
            {r.name}
            {r.samplePercent != null && (
              <span style={{ color: '#5C6473', marginLeft: 6, fontSize: '0.85em' }}>
                ({r.samplePercent}%)
              </span>
            )}
          </Dropdown.Item>
        ))}
      </SplitButton>
    </span>
  );
};

export default RunDiscoveryButton;
